import { useCallback, useEffect } from "react";

import {
	keepPreviousData,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";

import type { UseFormReturnTypeStrict } from "@/common/utils/UseFormReturnTypeStrict";
import { actionFetch } from "./actionFetch";
import { apiFetch } from "./apiFetch";
import { getActionMutationOptions } from "./getActionMutationOptions";
import { getActionQueryOptions } from "./getActionQueryOptions";
import { useActionFormValidate } from "./useActionFormValidate";
import { useApiFetchContext } from "./ApiFetchContext";

import type { ActionInput, ActionOutput } from "@/setup/backend/types";
import { useCacheStorage } from "../storage/CacheStorageContext";
import { isNetworkError } from "@/common/utils/is-network-error";
import { useCheckInOutOfflineCache } from "@/caregiver-app/components/hooks";
import { trpc } from "@/common/utils/trpc";

export const useStartImpersonatingUserMutation = () => {
	return useMutation(getActionMutationOptions("startImpersonatingUser"));
};

export const useGetCurrentUserQuery = () => {
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	return useQuery({
		...getActionQueryOptions("getCurrentUser", undefined, {
			batchEnabled: false,
			actionFetchOverride,
		}),
		retry: 1,
		placeholderData: keepPreviousData,
	});
};

export const useGetFrontendViewsQuery = (
	input: ActionInput<"getFrontendViews">,
) => {
	return useQuery(getActionQueryOptions("getFrontendViews", input));
};

export const useUpsertFrontendViewMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("upsertFrontendView"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getFrontendViews"],
			});
		},
	});
};

export const useDeleteFrontendViewMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteFrontendView"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getFrontendViews"],
			});
		},
	});
};

export const useQueryViewQuery = (
	input: ActionInput<"queryView">,
	{
		enabled,
		refetchInterval,
	}: { enabled?: boolean; refetchInterval?: number } = {},
) => {
	return useQuery({
		...getActionQueryOptions("queryView", input, { batchEnabled: false }),
		queryKey: ["queryView", input.frontendView.backendView, input],
		enabled,
		retry: false,
		refetchInterval: refetchInterval ? refetchInterval : false,
	});
};

export const useGetBackendViewQuery = (
	input: ActionInput<"getBackendView">,
) => {
	return useQuery(
		getActionQueryOptions("getBackendView", input, { batchEnabled: false }),
	);
};

export const useQueryOptionsViewQuery = (
	input: ActionInput<"queryOptionsView">,
	{ valueLookup }: { valueLookup?: boolean } = {},
) => {
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	return useQuery({
		...getActionQueryOptions("queryOptionsView", input, {
			actionFetchOverride,
		}),
		...(valueLookup
			? {
					queryKey: [
						"queryOptionsView",
						input.view,
						input.value,
						"valueLookup",
					],
				}
			: {}),
		enabled: valueLookup
			? Array.isArray(input.value)
				? input.value.length > 0
				: Boolean(input.value)
			: true,
	});
};

export const useCreatePayerSourceMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createPayerSource"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "payerSourcesView"],
			});
		},
	});
};

export const useGetPayerSourceQuery = (
	input: ActionInput<"getPayerSource">,
) => {
	return useQuery({
		...getActionQueryOptions("getPayerSource", input),
		enabled: Boolean(input.id),
	});
};

export const useGetRolesQuery = () => {
	return useQuery(getActionQueryOptions("getRoles", {}));
};

export const useUpdateRoleActionPermissionsMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateRoleActionPermissions"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getRoles", {}).queryKey,
			});
		},
	});
};

export const useGetActionsQuery = () => {
	return useQuery({
		queryKey: ["getActions"],
		queryFn: () => apiFetch<string[]>("/api/actions", "GET"),
	});
};

export const useGetClientQuery = (input: { id?: number }) => {
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	return useQuery({
		...getActionQueryOptions(
			"getClient",
			{
				id: input.id!,
			},
			{ actionFetchOverride },
		),
		enabled: Boolean(input.id),
	});
};

export const useGetCaregiverQuery = (input: ActionInput<"getCaregiver">) => {
	return useQuery(getActionQueryOptions("getCaregiver", input));
};

export const useUpdateClientMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateClient"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getClient", { id: input.id }).queryKey,
			});
		},
	});
};

export const useUpdateCaregiverMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateCaregiver"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiver", { id: input.id })
					.queryKey,
			});
		},
	});
};

export const useGetCaregiverAddressesQuery = (
	input: ActionInput<"getCaregiverAddresses">,
) => {
	return useQuery(getActionQueryOptions("getCaregiverAddresses", input));
};

export const useGetCaregiverFilesQuery = (
	input: ActionInput<"getCaregiverFiles">,
) => {
	return useQuery(getActionQueryOptions("getCaregiverFiles", input));
};

export const useGetCaregiverJournalEntriesQuery = (
	input: ActionInput<"getCaregiverJournalEntries">,
) => {
	return useQuery(getActionQueryOptions("getCaregiverJournalEntries", input));
};

export const useGetClientFilesQuery = (
	input: ActionInput<"getClientFiles">,
) => {
	return useQuery(getActionQueryOptions("getClientFiles", input));
};

export const useGetClientJournalEntriesQuery = (
	input: ActionInput<"getClientJournalEntries">,
) => {
	return useQuery(getActionQueryOptions("getClientJournalEntries", input));
};

export const useGetClientAddressesQuery = ({
	clientId,
}: Partial<ActionInput<"getClientAddresses">>) => {
	return useQuery({
		...getActionQueryOptions("getClientAddresses", { clientId: clientId! }),
		enabled: Boolean(clientId),
	});
};

export const useGetClientContactsQuery = (
	input: ActionInput<"getClientContacts">,
) => {
	return useQuery(getActionQueryOptions("getClientContacts", input));
};

export const useGetClientAuthorizationsQuery = (
	input: ActionInput<"getClientAuthorizations">,
) => {
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	return useQuery(
		getActionQueryOptions("getClientAuthorizations", input, {
			actionFetchOverride,
		}),
	);
};

export const useUpdateClientAddressMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateClientAddress"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientAddresses"],
			});
		},
	});
};

export const useUpdateClientAuthorizationMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateClientAuthorization"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientAuthorizations"],
			});
		},
	});
};

export const useUpdateClientContactMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateClientContact"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientContacts"],
			});
		},
	});
};

export const useUpdateClientFileMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateClientFile"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientFiles"],
			});
		},
	});
};

export const useUpdateClientJournalEntryMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateClientJournalEntry"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientJournalEntries"],
			});
		},
	});
};

export const useUpdateCaregiverAddressMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateCaregiverAddress"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getCaregiverAddresses"],
			});
		},
	});
};

export const useUpdateCaregiverFileMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateCaregiverFile"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getCaregiverFiles"],
			});
		},
	});
};

export const useUpdateCaregiverJournalEntryMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateCaregiverJournalEntry"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getCaregiverJournalEntries"],
			});
		},
	});
};

export const useCreateClientAddressMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createClientAddress"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getClientAddresses", {
					clientId: input.clientId,
				}).queryKey,
			});
		},
	});
};

export const useCreateClientAuthorizationMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createClientAuthorization"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getClientAuthorizations", {
					clientId: input.clientId,
				}).queryKey,
			});
		},
	});
};

export const useCreateClientContactMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createClientContact"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getClientContacts", {
					clientId: input.clientId,
				}).queryKey,
			});
		},
	});
};

export const useCreateClientFileMutation = () => {
	const queryClient = useQueryClient();

	const options = getActionMutationOptions("createClientFile", {});
	return useMutation({
		...options,
		mutationFn: (input: ActionInput<"createClientFile"> & { file: File }) => {
			if (!input.file) {
				throw new Error("No file provided");
			}

			return options.mutationFn!({
				...input,
				filename: input.file.name,
				size: input.file.size,
				contentType: input.file.type,
			}).then((actionRes) => {
				return fetch(actionRes.url, {
					method: "PUT",
					body: input.file,
					headers: {
						"Content-Length": input.file.size.toString(),
						"Content-Type": input.file.type,
					},
				})
					.then((res) => {
						if (res.ok) {
							return actionRes;
						}
						throw new Error(res.statusText);
					})
					.catch((err) => {
						return actionFetch("deleteClientFile", {
							id: actionRes.id,
						}).finally(() => {
							throw err;
						});
					});
			});
		},
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getClientFiles", {
					clientId: input.clientId,
				}).queryKey,
			});
		},
	});
};

export const useCreateClientFileFormValidate = (
	form: UseFormReturnTypeStrict<
		ActionInput<"createClientFile"> & { file: File }
	>,
) => {
	useActionFormValidate("createClientFile", {
		values: form.values,
		setFieldError: form.setFieldError,
		clearFieldError: form.clearFieldError,
		setErrors: form.setErrors,
		getTransformedValues: () => {
			const { file, ...rest } = form.getTransformedValues(form.values);
			return rest;
		},
	});
	useEffect(() => {
		if (!form.values.file) {
			form.setFieldError("file", "File is required");
		} else {
			form.clearFieldError("file");
		}
	}, [form.values]);
};

export const useCreateClientJournalEntryMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createClientJournalEntry"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getClientJournalEntries", {
					clientId: input.clientId,
				}).queryKey,
			});
		},
	});
};

export const useCreateCaregiverAddressMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createCaregiverAddress"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiverAddresses", {
					caregiverId: input.caregiverId,
				}).queryKey,
			});
		},
	});
};

export const useCreateCaregiverFileMutation = () => {
	const queryClient = useQueryClient();

	const options = getActionMutationOptions("createCaregiverFile", {});
	return useMutation({
		...options,
		mutationFn: (
			input: ActionInput<"createCaregiverFile"> & { file: File },
		) => {
			if (!input.file) {
				throw new Error("No file provided");
			}

			return options.mutationFn!({
				...input,
				filename: input.file.name,
				size: input.file.size,
				contentType: input.file.type,
			}).then((actionRes) => {
				return fetch(actionRes.url, {
					method: "PUT",
					body: input.file,
					headers: {
						"Content-Length": input.file.size.toString(),
						"Content-Type": input.file.type,
					},
				})
					.then((res) => {
						if (res.ok) {
							return actionRes;
						}
						throw new Error(res.statusText);
					})
					.catch((err) => {
						return actionFetch("deleteCaregiverFile", {
							id: actionRes.id,
						}).finally(() => {
							throw err;
						});
					});
			});
		},
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiverFiles", {
					caregiverId: input.caregiverId,
				}).queryKey,
			});
		},
	});
};

export const useCreateCaregiverFileFormValidate = (
	form: UseFormReturnTypeStrict<
		ActionInput<"createCaregiverFile"> & { file: File }
	>,
) => {
	useActionFormValidate("createCaregiverFile", {
		values: form.values,
		setFieldError: form.setFieldError,
		clearFieldError: form.clearFieldError,
		setErrors: form.setErrors,
		getTransformedValues: () => {
			const { file, ...rest } = form.getTransformedValues(form.values);
			return rest;
		},
	});
	useEffect(() => {
		if (!form.values.file) {
			form.setFieldError("file", "File is required");
		} else {
			form.clearFieldError("file");
		}
	}, [form.values]);
};

export const useCreateCaregiverJournalEntryMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createCaregiverJournalEntry"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiverJournalEntries", {
					caregiverId: input.caregiverId,
				}).queryKey,
			});
		},
	});
};

export const useDeleteClientAddressMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteClientAddress"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientAddresses"],
			});
		},
	});
};

export const useDeleteClientAuthorizationMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteClientAuthorization"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientAuthorizations"],
			});
		},
	});
};

export const useDeleteClientContactMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteClientContact"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientContacts"],
			});
		},
	});
};

export const useDeleteClientFileMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteClientFile"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientFiles"],
			});
		},
	});
};

export const useDeleteClientJournalEntryMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteClientJournalEntry"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getClientJournalEntries"],
			});
		},
	});
};

export const useDeleteCaregiverAddressMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteCaregiverAddress"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getCaregiverAddresses"],
			});
		},
	});
};

export const useDeleteCaregiverFileMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteCaregiverFile"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getCaregiverFiles"],
			});
		},
	});
};

export const useDeleteCaregiverJournalEntryMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteCaregiverJournalEntry"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getCaregiverJournalEntries"],
			});
		},
	});
};

export const useDownloadClientFile = () => {
	const options = getActionMutationOptions("getClientFileDownloadLink", {});
	return useMutation({
		...options,
		mutationFn: ({ id }) =>
			options.mutationFn!({ id }).then((res) => {
				window.open(res.url, "_blank");
				return res;
			}),
	});
};

export const useDownloadCaregiverFile = () => {
	const options = getActionMutationOptions("getCaregiverFileDownloadLink", {});
	return useMutation({
		...options,
		mutationFn: ({ id }) =>
			options.mutationFn!({ id }).then((res) => {
				window.open(res.url, "_blank");
				return res;
			}),
	});
};

export const useSendCaregiverLoginLinkMutation = () => {
	return useMutation(getActionMutationOptions("sendCaregiverLoginLink"));
};

export const useSetCaregiverPasswordMutation = () => {
	return useMutation(getActionMutationOptions("setCaregiverPassword"));
};

export const useCreateCaregiverMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createCaregiver"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "caregiversView"],
			});
		},
	});
};

export const useCreateClientMutation = () => {
	const queryClient = useQueryClient();
	const utils = trpc.useUtils();
	return useMutation({
		...getActionMutationOptions("createClient"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "clientsView"],
			});
			utils.clients.selectOptions.invalidate();
		},
	});
};

export const useGetShiftQuery = (input: Partial<ActionInput<"getShift">>) => {
	return useQuery({
		...getActionQueryOptions("getShift", {
			id: input.id!,
		}),
		enabled: input.id !== undefined,
		placeholderData: undefined,
	});
};

export const useCreateShiftMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createShift"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "shiftsView"],
			});
		},
	});
};

export const useUpdateShiftMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateShift"),
		onSuccess: (_, input) => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "shiftsView"],
			});
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getShift", { id: input.id! }).queryKey,
			});
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getShiftChanges", {
					scheduleId: input.id!,
				}).queryKey,
			});
		},
	});
};

export const useDeleteShiftMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteShift"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "shiftsView"],
			});
		},
	});
};

export const useCreateShiftCommentMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createShiftComment"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getShiftComments"],
			});
		},
	});
};

export const useUpdateShiftCommentMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateShiftComment"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getShiftComments"],
			});
		},
	});
};

export const useDeleteShiftCommentMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteShiftComment"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getShiftComments"],
			});
		},
	});
};

export const useSendShiftReminderMutation = () => {
	return useMutation(getActionMutationOptions("sendShiftReminder"));
};

export const useSendPickupInvitationsMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("sendPickupInvitations"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getShiftPickupInvitations"],
			});
		},
	});
};

export const useGetShiftCommentsQuery = (
	input: ActionInput<"getShiftComments">,
) => {
	return useQuery(getActionQueryOptions("getShiftComments", input));
};

export const useDeleteTimesheetMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteTimesheet"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "shiftsView"],
			});
			queryClient.invalidateQueries({
				queryKey: ["getShift"],
			});
		},
	});
};

export const useDeleteSignaturesMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteSignatures"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "shiftsView"],
			});
			queryClient.invalidateQueries({
				queryKey: ["getShift"],
			});
		},
	});
};

export const useGetShiftChangesQuery = ({
	scheduleId,
}: Partial<ActionInput<"getShiftChanges">>) => {
	return useQuery({
		...getActionQueryOptions("getShiftChanges", { scheduleId: scheduleId! }),
		enabled: Boolean(scheduleId),
	});
};

export const useGetShiftPickupInvitationsQuery = ({
	scheduleId,
}: Partial<ActionInput<"getShiftPickupInvitations">>) => {
	return useQuery({
		...getActionQueryOptions("getShiftPickupInvitations", {
			scheduleId: scheduleId!,
		}),
		enabled: Boolean(scheduleId),
	});
};

export const useGetClientAuthorizationMatchQuery = (
	input: Partial<ActionInput<"getClientAuthorizationMatch">>,
) => {
	return useQuery({
		...getActionQueryOptions(
			"getClientAuthorizationMatch",
			input as ActionInput<"getClientAuthorizationMatch">,
		),
		enabled:
			Boolean(input.clientId) &&
			Boolean(input.payerSourceId) &&
			Boolean(input.startDate) &&
			Boolean(input.endDate),
	});
};

export const useGetFrontendViewUserPreferencesQuery = (
	input: ActionInput<"getFrontendViewUserPreferences">,
) => {
	return useQuery(
		getActionQueryOptions("getFrontendViewUserPreferences", input),
	);
};

export const useUpsertFrontendViewUserPreferenceMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("upsertFrontendViewUserPreference"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["getFrontendViewUserPreferences"],
			});
		},
	});
};

export const useCaregiverAppGetShiftsQuery = (
	input: ActionInput<"caregiverAppGetShifts">,
) => {
	const cache = useCacheStorage();
	const { getCheckInShiftIds, getCheckOutShiftIds, getCheckIn, getCheckOut } =
		useCheckInOutOfflineCache();
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	const fetchFn = actionFetchOverride ?? actionFetch;

	return useInfiniteQuery({
		queryKey: ["caregiverAppGetShifts", input.after, input.status],
		queryFn: ({ pageParam: { status: _status, ...unfilteredPageParam } }) =>
			(
				fetchFn(
					"caregiverAppGetShifts",
					// status is filtered locally below. The server response must stay unfiltered because
					// every status shares the same offline cache entry, which is seeded as the scheduled query on launch.
					unfilteredPageParam,
					false,
					false,
				)
					.then((data) => {
						if (
							import.meta.env.VITE_CAPACITOR_BUILD === "true" ||
							import.meta.env.VITE_PWA_BUILD === "true"
						) {
							cache.setItem(
								`caregiverAppGetShifts-${unfilteredPageParam.after}-${unfilteredPageParam.before}`,
								JSON.stringify(data),
							);
						}

						return data;
					})
					.catch((e) => {
						if (isNetworkError(e)) {
							return cache
								.getItem(
									`caregiverAppGetShifts-${unfilteredPageParam.after}-${unfilteredPageParam.before}`,
								)
								.then((data) => {
									if (data) {
										return JSON.parse(data);
									}
									throw e;
								});
						}
						throw e;
					}) as Promise<ActionOutput<"caregiverAppGetShifts">>
			).then(async (data) => {
				// we filter locally because the fitler is so simple and usually returns a subset of the exact same data
				// net result is better performance and smoother offline cache
				let filteredData = data;
				if (input.status && input.status !== "scheduled") {
					filteredData = data.filter(
						(shift) => shift.tasksMissing || shift.timeEntryMissing,
					);
				}

				// Apply offline check-in/out data to the fetched data
				try {
					const [checkInShiftIds, checkOutShiftIds] = await Promise.all([
						getCheckInShiftIds(),
						getCheckOutShiftIds(),
					]);

					const allOfflineShiftIds = [
						...new Set([...checkInShiftIds, ...checkOutShiftIds]),
					];

					if (allOfflineShiftIds.length > 0) {
						// Apply offline data to each shift that has it
						const updatedData = await Promise.all(
							filteredData.map(async (shift) => {
								if (allOfflineShiftIds.includes(shift.id)) {
									const [checkInData, checkOutData] = await Promise.all([
										getCheckIn(shift.id),
										getCheckOut(shift.id),
									]);

									const checkInTime =
										checkOutData?.checkInOut?.checkIn ||
										checkInData?.checkInOut?.checkIn;
									const checkOutTime = checkOutData?.checkInOut?.checkOut;

									if (checkInTime || checkOutTime) {
										return {
											...shift,
											checkIn: checkInTime || shift.checkIn,
											offlineCheckIn: !!checkInTime,
											checkOut: checkOutTime || shift.checkOut,
											offlineCheckOut: !!checkOutTime,
										};
									}
								}
								return shift;
							}),
						);

						return updatedData;
					}
				} catch (error) {
					// If offline data retrieval fails, return the original data
					console.warn("Failed to apply offline data to shifts:", error);
				}

				return filteredData;
			}),
		initialPageParam: input,
		getNextPageParam: (lastPage) =>
			lastPage.length === 0
				? null
				: {
						...input,
						before: undefined,
						after: lastPage[lastPage.length - 1].startTimestamp,
					},
		getPreviousPageParam: (firstPage) =>
			firstPage.length === 0
				? null
				: {
						...input,
						after: undefined,
						before: firstPage[0].startTimestamp,
					},

		maxPages: 3,
		staleTime: 1000 * 60,
		placeholderData: keepPreviousData,
		networkMode: "always",
		refetchOnReconnect: true,
	});
};

export const useInvalidateCaregiverAppShiftsQuery = () => {
	const queryClient = useQueryClient();
	return () => {
		queryClient.invalidateQueries({
			queryKey: ["caregiverAppGetShifts"],
		});
	};
};

export const caregiverAppMocks = {
	pickupInvitationsLong: "caregiver-app-pickup-invitations-long",
} satisfies Record<string, string>;

export const useCaregiverAppGetPickupInvitationsQuery = () => {
	const options = getActionQueryOptions("caregiverAppGetPickupInvitations", {});
	return useQuery({
		...options,
		queryFn: async (args) => {
			return typeof options.queryFn === "function" ? options.queryFn(args) : [];
		},
	});
};

export const useCaregiverAppCheckInOutMutation = () => {
	const queryClient = useQueryClient();
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	return useMutation({
		...getActionMutationOptions("caregiverAppCheckInOut", {
			actionFetchOverride,
		}),
		onSuccess: (data, vars) => {
			queryClient.invalidateQueries({
				queryKey: ["caregiverAppGetShifts"],
			});

			if (data.careSheetId && vars.checkOut) {
				// hard link to the checkout page to avoid the router from intercepting the navigation
				window.location.href = `/app/care-sheets/${data.careSheetId}/shifts/${data.scheduleId}/checkout`;
			}
		},
	});
};

export const useCaregiverAppAcceptPickupInvitationMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("caregiverAppAcceptPickupInvitation"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["caregiverAppGetPickupInvitations"],
			});
			queryClient.invalidateQueries({
				queryKey: ["caregiverAppGetShifts"],
			});
		},
	});
};

export const useGetNurseAssessmentsQuery = (clientId: number) => {
	return useQuery(getActionQueryOptions("getNurseAssessments", { clientId }));
};

export const useGetCaregiverCashAdvancesQuery = (caregiverId: number) => {
	return useQuery(
		getActionQueryOptions("getCaregiverCashAdvances", { caregiverId }),
	);
};

export const useUpdateNurseAssessmentMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateNurseAssessment"),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getNurseAssessments", {
					clientId: data.clientId,
				}).queryKey,
			});
		},
	});
};

export const useUpdateCaregiverCashAdvanceMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateCaregiverCashAdvance"),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiverCashAdvances", {
					caregiverId: data.caregiverId,
				}).queryKey,
			});
		},
	});
};

export const useDeleteCaregiverCashAdvanceMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("deleteCaregiverCashAdvance"),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiverCashAdvances", {
					caregiverId: data.caregiverId,
				}).queryKey,
			});
		},
	});
};

export const useCreateNurseAssessmentMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createNurseAssessment"),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getNurseAssessments", {
					clientId: data.clientId,
				}).queryKey,
			});
		},
	});
};

export const useCreateCaregiverCashAdvanceMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("createCaregiverCashAdvance"),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getCaregiverCashAdvances", {
					caregiverId: data.caregiverId,
				}).queryKey,
			});
		},
	});
};

export const useUpdateFileExportTypeMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateFileExportType"),
		onSuccess: (data) => {
			queryClient.invalidateQueries({
				queryKey: getActionQueryOptions("getFileExportType", {
					name: data.name,
				}).queryKey,
			});
			queryClient.invalidateQueries({
				queryKey: ["queryView", "fileExportTypesView"],
			});
		},
	});
};

export const useGetFileExportTypeQuery = (
	input: ActionInput<"getFileExportType">,
	enabled = true,
) => {
	return useQuery({
		...getActionQueryOptions("getFileExportType", input),
		enabled,
	});
};

export const useGetFileExportQuery = (
	input: ActionInput<"getFileExport">,
	enabled = true,
) => {
	return useQuery({
		...getActionQueryOptions("getFileExport", input),
		enabled,
	});
};

export const useQueueFileExportMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("queueFileExport"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "fileExportsView"],
			});
			queryClient.invalidateQueries({
				queryKey: ["getFileExport"],
			});
		},
	});
};

export const useGetFileExportDownloadLinkMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("getFileExportDownloadLink"),
		onSuccess: (data) => {
			window.open(data.downloadUrl, "_blank");
			queryClient.invalidateQueries({
				queryKey: ["getFileExport"],
			});
		},
	});
};

export const useGetFileImportDownloadLinkMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("getFileImportDownloadLink"),
		onSuccess: (data) => {
			window.open(data.downloadUrl, "_blank");
			queryClient.invalidateQueries({
				queryKey: ["getFileImport"],
			});
		},
	});
};

export const useGetFileImportQuery = (
	input: ActionInput<"getFileImport">,
	enabled = true,
) => {
	return useQuery({
		...getActionQueryOptions("getFileImport", input),
		enabled,
	});
};

export const useGetFileImportTypeQuery = (
	input: ActionInput<"getFileImportType">,
	enabled = true,
) => {
	return useQuery({
		...getActionQueryOptions("getFileImportType", input),
		enabled,
	});
};

export const useQueueFileImportMutation = () => {
	const queryClient = useQueryClient();

	const options = getActionMutationOptions("queueFileImport", {});
	return useMutation({
		mutationFn: (
			input: Pick<ActionInput<"queueFileImport">, "type"> & { file: File },
		) => {
			if (!input.file) {
				throw new Error("No file provided");
			}

			return options.mutationFn!({
				type: input.type,
				fileName: input.file.name,
				fileSizeBytes: input.file.size,
				contentType: input.file.type,
			}).then((actionRes) => {
				return fetch(actionRes.uploadUrl, {
					method: "PUT",
					body: input.file,
					headers: {
						"Content-Length": input.file.size.toString(),
						"Content-Type": input.file.type,
					},
				}).then((res) => {
					if (res.ok) {
						return actionRes;
					}
					throw new Error(res.statusText);
				});
			});
		},
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "fileImportsView"],
			});
			queryClient.invalidateQueries({
				queryKey: ["getFileImport"],
			});
		},
	});
};

export const useUpdateFileImportTypeMutation = () => {
	const queryClient = useQueryClient();
	return useMutation({
		...getActionMutationOptions("updateFileImportType"),
		onSuccess: () => {
			queryClient.invalidateQueries({
				queryKey: ["queryView", "fileImportTypesView"],
			});

			queryClient.invalidateQueries({
				queryKey: ["getFileImportType"],
			});
		},
	});
};

export const useGetFileImportTemplateDownloadLink = (
	input: ActionInput<"getFileImportTemplateDownloadLink">,
	enabled = true,
) => {
	return useQuery({
		...getActionQueryOptions("getFileImportTemplateDownloadLink", input),
		enabled,
	});
};

export const useGetMapLocationsQuery = (
	input: ActionInput<"getMapLocations">,
	{ enabled = true } = {},
) => {
	return useQuery({
		...getActionQueryOptions("getMapLocations", input),
		staleTime: 1000 * 60 * 60, // 1 hour
		enabled,
	});
};

export const useGetInAppNotificationsQuery = () => {
	return useQuery(getActionQueryOptions("getInAppNotifications", {}));
};

export const useGetChatUnreadCountQuery = () => {
	return useQuery({
		...getActionQueryOptions("getChatUnreadCount", {}),
		staleTime: 60_000,
	});
};

export const useInvalidateChatUnreadCount = () => {
	const queryClient = useQueryClient();
	return useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ["getChatUnreadCount"] });
	}, [queryClient]);
};

export const useGetClientAuthorizationCodeCombosQuery = (
	input: ActionInput<"getClientAuthorizationCodeCombos">,
) => {
	const actionFetchOverride = useApiFetchContext()?.actionFetch;
	return useQuery(
		getActionQueryOptions("getClientAuthorizationCodeCombos", input, {
			actionFetchOverride,
		}),
	);
};

export const useSendClientLoginLinkMutation = () => {
	return useMutation(getActionMutationOptions("sendClientLoginLink"));
};

export const useSendClientContactLoginLinkMutation = () => {
	return useMutation(getActionMutationOptions("sendClientContactLoginLink"));
};
